/**
 * Keeping track of recording state
 */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useApiStore } from '@/stores/Api'

import logger from '@/utilities/Logger'

import type { ValidateTokenExtendedResponse } from '@/TcxApi/Authentication'
import type { PbxRecordingStatusResponse } from '@/TcxApi/PbxRecording'
import { sleep } from '@/utilities/Tools'

export const useRecordingStateStore = defineStore('recordingState', () => {
	logger.debug('RecordingState: Initializing store')

	const realtime = useRealtimeStore()
	const ss = useSettingsStore()
	const api = useApiStore()

	/** List of channel id for the channels that are being recorded */
	const recordingIds = ref<string[]>([])

	/** True when user is recording */
	const isRecording = computed(() => { return recordingIds.value.length > 0 })

	if (realtime.connected) { init(true) }

	async function init(initial = false) {
		await sleep(1)
		await ss.ready()

		if (initial) {
			realtime.pbxEvents.on('RECORDING_START', event => {
				if (event.employee === ss.auth?.employee) {
					recordingIds.value.push(event.channel)
					logger.info(`RecordingState: Employee is recording the call with id: ${event.channel}`)
				}
			})

			realtime.pbxEvents.on('RECORDING_END', event => {
				if (event.employee === ss.auth?.employee) {
					const index = recordingIds.value.findIndex(recording => recording === event.channel)
					if (index !== -1) { recordingIds.value.splice(index, 1) }
					logger.info('RecordingState: Employee stopped recording the call with id: ' + event.channel)
				}
			})

			realtime.configEvents.on('EMPLOYEE_UPDATED', async (event) => {
				if (ss.auth && event.id === ss.auth.employee) {
					try {
						const res = await api.get<ValidateTokenExtendedResponse>(`/auth/validateToken?extend=true&token=${ss.token}`)

						ss.auth.recordingMode = res.data.recordingMode
					}
					catch (_) { /** Ignore */ }
				}
			})

			realtime.realtimeEvents.on('Connected', () => { init() })
		}

		try {
			const res = await api.get<PbxRecordingStatusResponse>('/pbx/recording/status')

			recordingIds.value = []

			for (const item of res.data) {
				if (item.recording) {
					recordingIds.value.push(item.channel)
				}
			}
			logger.info('RecordingState: Initialized - currently we are ' + (isRecording.value ? 'recording ' + recordingIds.value.length + ' calls' : 'not recording'))
		}
		catch (_) { /**	Ignore */ }
	}

	return { isRecording, recordingIds }
})
